import {
  ChangeEvent,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useId,
  useMemo,
  useState,
} from 'react';

import {
  InputBase,
  InputProps,
  Typography,
  TypographyProps,
  styled,
} from '@material-ui/core';

type Item = {
  value: string;
  label?: string;
};

type AutocompleteInputProps = Pick<
  InputProps,
  'value' | 'onChange' | 'onKeyDown' | 'inputProps'
> & {
  'data-testid': string;
};

interface AutocompleteContext<TItem extends Item = Item> {
  getInputProps: () => AutocompleteInputProps;
  getListboxProps: () => Record<string, unknown>;
  getOptionProps: (index: number) => Record<string, unknown>;
  multiple: boolean;
  items: TItem[];
  highlightedIndex: number;
  onClose: () => void;
}

// Context
const CONTEXT = createContext<AutocompleteContext | null>(null);

export const useAutocompleteContext = () => {
  const context = useContext(CONTEXT);
  if (!context) {
    throw new Error(
      'Autocomplete compound components must be used within an Autocomplete.Root component',
    );
  }
  return context;
};

// Styled Components
const StyledInput = styled(InputBase)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

// Components

const Input = ({ inputProps, ...props }: InputProps) => {
  const { getInputProps } = useAutocompleteContext();
  const { inputProps: contextInputProps, ...contextProps } = getInputProps();
  return (
    <StyledInput
      {...contextProps}
      {...props}
      inputProps={{
        'aria-label': props.placeholder,
        ...contextInputProps,
        ...inputProps,
      }}
    />
  );
};

const StyledEmpty = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(1.5),
}));

export type EmptyProps = TypographyProps & {
  disableTypography?: boolean;
};

const Empty = ({ ...props }: EmptyProps) => {
  const { items } = useAutocompleteContext();
  const { disableTypography, children, ...rest } = props;

  if (items.length > 0) {
    return null;
  }

  if (disableTypography) {
    return <>{children || 'No results'}</>;
  }

  return <StyledEmpty {...rest}>{children || 'No results'}</StyledEmpty>;
};

export interface RootProps<
  TOption extends Item,
  Multiple extends boolean = false,
> {
  children: (options: AutocompleteContext<TOption>) => ReactNode;
  items: TOption[];
  multiple?: Multiple;
  value?: Multiple extends true ? string[] : string | null;
  onValueChange?: (
    value: Multiple extends true ? string[] : string | null,
  ) => void;
  onClose?: () => void;
  onInputChange?: (value: string, e?: ChangeEvent<HTMLInputElement>) => void;
  // Return true to prevent default behavior.
  onSelect?: (value: string, e: MouseEvent | KeyboardEvent) => boolean;
  // Runs before the built-in key handling. Return true to mark the key as
  // handled and skip the built-in behavior.
  onKeyDown?: (
    e: KeyboardEvent,
    context: { highlightedItem: TOption | undefined; inputValue: string },
  ) => boolean;
  disableFilter?: boolean;
}

const Root = <TOption extends Item, Multiple extends boolean = false>({
  children,
  value,
  onValueChange,
  onInputChange,
  items: initialItems,
  multiple = false as Multiple,
  onClose,
  onSelect,
  onKeyDown,
  disableFilter = false,
}: RootProps<TOption, Multiple>) => {
  const [inputValue, setInputValue] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const listboxId = useId();
  const optionId = useCallback(
    (index: number) => `${listboxId}-option-${index}`,
    [listboxId],
  );

  const selectedValues = useMemo(() => {
    if (multiple) {
      return (value as string[]) || [];
    }
    return value ? [value as string] : [];
  }, [value, multiple]);

  const items = useMemo(() => {
    if (disableFilter) {
      return initialItems;
    }

    return initialItems.filter(option => {
      const searchText = (option.label || option.value).toLowerCase();
      return searchText.includes(inputValue.toLowerCase());
    });
  }, [initialItems, inputValue, disableFilter]);

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onInputChange?.(event.target.value, event);
      if (event.isDefaultPrevented()) {
        return;
      }

      setInputValue(event.target.value);
      setHighlightedIndex(-1);
    },
    [onInputChange],
  );

  const handleOptionSelect = useCallback(
    (optionValue: string, e: MouseEvent | KeyboardEvent) => {
      setInputValue('');
      onInputChange?.('');

      if (onSelect) {
        const preventDefault = onSelect(optionValue, e);
        if (preventDefault) {
          return;
        }
      }

      // TypeScript cannot narrow the conditional type `Multiple extends true ? string[] : string | null`
      // inside the function body, so we cast through the resolved union type.
      type Value = Multiple extends true ? string[] : string | null;
      const emit = onValueChange as
        | ((value: string | string[] | null) => void)
        | undefined;

      if (multiple) {
        if (!e.shiftKey) {
          emit?.([optionValue] as Value);
        } else {
          const newValue = selectedValues.includes(optionValue)
            ? selectedValues.filter(v => v !== optionValue)
            : [...selectedValues, optionValue];
          emit?.(newValue as Value);
        }
      } else {
        emit?.(optionValue as Value);
      }
    },
    [onInputChange, onSelect, onValueChange, multiple, selectedValues],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const highlightedItem =
        highlightedIndex >= 0 ? items[highlightedIndex] : undefined;
      if (onKeyDown?.(event, { highlightedItem, inputValue })) {
        return;
      }

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setHighlightedIndex(prev =>
            prev < items.length - 1 ? prev + 1 : prev,
          );
          break;
        case 'ArrowUp':
          event.preventDefault();
          setHighlightedIndex(prev => (prev > 0 ? prev - 1 : prev));
          break;
        case 'Enter':
          event.preventDefault();
          if (highlightedIndex >= 0 && highlightedIndex < items.length) {
            handleOptionSelect(items[highlightedIndex].value, event);
          }
          break;
        case 'Escape':
          onClose?.();
          break;
        default:
          break;
      }
    },
    [
      highlightedIndex,
      items,
      inputValue,
      onKeyDown,
      onClose,
      handleOptionSelect,
    ],
  );

  const contextValue = useMemo(
    () => ({
      getInputProps: () => ({
        value: inputValue,
        onChange: handleInputChange,
        onKeyDown: handleKeyDown,
        inputProps: {
          role: 'combobox',
          'aria-expanded': true,
          'aria-controls': listboxId,
          'aria-autocomplete': 'list' as const,
          'aria-activedescendant':
            highlightedIndex >= 0 && highlightedIndex < items.length
              ? optionId(highlightedIndex)
              : undefined,
        },
        'data-testid': 'simple-autocomplete-input',
      }),
      getListboxProps: () => ({
        id: listboxId,
        role: 'listbox',
        'aria-multiselectable': multiple,
        'data-testid': 'simple-autocomplete-listbox',
      }),
      getOptionProps: (index: number) => ({
        id: optionId(index),
        role: 'option',
        'aria-posinset': index + 1,
        'aria-setsize': items.length,
        selected:
          selectedValues.includes(items[index].value) ||
          index === highlightedIndex,
        onClick: (e: MouseEvent) => {
          // MUI ButtonBase synthesises "clicks" from Enter keydowns after
          // calling preventDefault on them, so only let real mouse clicks
          // be cancelled via preventDefault.
          if (e.type === 'click' && e.isDefaultPrevented()) {
            return;
          }

          handleOptionSelect(items[index].value, e);
        },
        'aria-selected': selectedValues.includes(items[index].value),
        'data-highlighted': index === highlightedIndex,
      }),
      onClose: () => onClose?.(),
      items,
      highlightedIndex,
      multiple,
    }),
    [
      inputValue,
      selectedValues,
      multiple,
      items,
      highlightedIndex,
      handleOptionSelect,
      handleInputChange,
      handleKeyDown,
      onClose,
      listboxId,
      optionId,
    ],
  );

  return (
    <CONTEXT.Provider value={contextValue}>
      {children(contextValue)}
    </CONTEXT.Provider>
  );
};

// Export
export const SimpleAutocomplete = {
  Root,
  Input,
  Empty,
};
